import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateField } from "../component/aces/DateField";
import { apiError, exportAudit, searchAudit } from "../services/AiService";
import { AuditEntry } from "../types/ai";
import { saveBlob } from "../utils/secureFile";
import { AuditTable, ErrorAlert, Loading, PageShell } from "../component/aces/Shared";

const ENTITY_TYPES = ["AI_ANALYSIS", "FINDING", "CAPA", "AI_REPORT", "CHECKLIST", "USER_PERMISSION", "TENANT_AI_SETTINGS"];
const PAGE_SIZE = 50;

const AuditScreen = () => {
  const { t } = useTranslation();
  const [entityType, setEntityType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState<{ items: AuditEntry[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    searchAudit({ entityType: entityType || undefined, from: from || undefined, to: to || undefined, page, size: PAGE_SIZE })
      .then((r) => setData({ items: r.items, total: r.total }))
      .catch((e) => {
        setError(apiError(e));
        setData({ items: [], total: 0 });
      });
  }, [entityType, from, to, page]);

  const download = async () => {
    try {
      saveBlob(await exportAudit({ entityType: entityType || undefined, from: from || undefined, to: to || undefined }), "audit-trail.csv");
    } catch (e) {
      setError(apiError(e));
    }
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  return (
    <PageShell title={t("aces.audit.title")} actions={<button className="aces-btn secondary" onClick={download}><i className="ri-download-2-line"></i> CSV</button>}>
      <ErrorAlert message={error} onClose={() => setError(null)} />
      <div className="aces-actions mb-3">
        <select className="form-control" style={{ maxWidth: 240 }} value={entityType} onChange={(e) => { setPage(0); setEntityType(e.target.value); }}>
          <option value="">{t("aces.audit.allRecords")}</option>
          {ENTITY_TYPES.map((x) => <option key={x} value={x}>{t(`aces.audit.types.${x}`)}</option>)}
        </select>
        <label className="aces-muted" htmlFor="audit-from">{t("aces.audit.from")}</label>
        <div style={{ maxWidth: 170 }}><DateField id="audit-from" value={from} onChange={(v) => { setPage(0); setFrom(v); }} /></div>
        <label className="aces-muted" htmlFor="audit-to">{t("aces.audit.to")}</label>
        <div style={{ maxWidth: 170 }}><DateField id="audit-to" value={to} onChange={(v) => { setPage(0); setTo(v); }} /></div>
      </div>
      {data === null ? <Loading /> : (
        <>
          <AuditTable entries={data.items} />
          <div className="aces-actions mt-2">
            <button className="aces-btn small secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>‹</button>
            <span className="aces-muted">{page + 1} / {pages} · {data.total}</span>
            <button className="aces-btn small secondary" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>›</button>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default AuditScreen;
